// src/pages/onboarding/OnboardingForm.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onboardingAPI } from '../../services/api/onboarding.service';
import DocumentUpload from './components/DocumentUpload';
import Logo from '../../assets/images/logonegro.svg';
import { useAuth } from "../../context/AuthContext";
import Footer from '../../components/layout/footer';
import { getCurrentApiUrl } from '../../config/server.config';
interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  documentType?: string;
  documentInfo?: any;
}

interface OnboardingState {
  sessionId: string;
  accountType?: 'EXPRESS' | 'INDIVIDUAL' | 'CORPORATE';
  currentStage: string;
  preferredLanguage: string;
  documentProgress: string[];
  selectedIdType?: string;
}

const OnboardingForm: React.FC = () => {
  const { logout } = useAuth();
   const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState<string[]>([]);
  const [state, setState] = useState<OnboardingState>({
    sessionId: '',
    currentStage: 'INITIAL',
    preferredLanguage: 'en',
    documentProgress: []
  });
  const [requiredDocumentType, setRequiredDocumentType] = useState('');
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [showTermsButton, setShowTermsButton] = useState(false);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  // 🔧 FIX: Flag para evitar múltiples llamadas simultáneas a startChat
  const startChatCalled = useRef(false);
  const redirectingToDashboard = useRef(false); // ← NUEVO: Prevenir bucle infinito

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    // 🔍 DEBUG: Monitor messages array changes
    console.log('🔍 MESSAGES ARRAY CHANGED:', {
      count: messages.length,
      messages: messages.map(m => ({ role: m.role, contentPreview: m.content?.substring(0, 50) }))
    });
  }, [messages]);

  useEffect(() => {
    // 🚨 FIX: SIEMPRE resetear el flag al montar el componente
    // Esto asegura que startChat se ejecute cada vez que el usuario entra al onboarding
    console.log('🔧 ONBOARDING: Component mounted, resetting startChatCalled flag');
    startChatCalled.current = false;

    // Solo ejecutar startChat una vez (incluso en React StrictMode que renderiza 2 veces)
    if (!startChatCalled.current) {
      startChatCalled.current = true;
      console.log('🚀 ONBOARDING: Calling startChat()...');

      // Pequeño delay para asegurar que solo se ejecuta una vez
      const timer = setTimeout(() => {
        startChat();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, []);

  const startChat = async () => {
    setLoading(true);
    try {
      console.log('🚀 ONBOARDING: startChat iniciado');

      // 🏢 CRITICAL: Check if user is CORPORATE BEFORE calling SUBUTAI
      try {
        const token = localStorage.getItem('token');
        const corporateCheckResponse = await fetch(`${getCurrentApiUrl()}/onboarding/corporate/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (corporateCheckResponse.ok) {
          const corporateData = await corporateCheckResponse.json();
          if (corporateData.success && corporateData.data.accountType === 'CORPORATE') {
            console.log('🏢 CRITICAL: Usuario CORPORATE detectado');
            console.log('🏢 Corporate data:', corporateData.data);

            // 🔥 CHECK IF COMPLETE FIRST
            if (corporateData.data.isComplete) {
              if (!redirectingToDashboard.current) {
                redirectingToDashboard.current = true;
                console.log('✅ Corporate onboarding COMPLETE - going to dashboard');
                navigate('/dashboard', { replace: true });
              }
              return; // STOP HERE
            }

            // Onboarding NOT complete - redirect to form
            console.log('→ Corporate onboarding INCOMPLETE - redirecting to form');

            // 🌍 Guardar el idioma del usuario en localStorage
            if (corporateData.data.language) {
              localStorage.setItem('corporateOnboardingLanguage', corporateData.data.language);
              console.log('✅ Idioma cargado desde BD:', corporateData.data.language);
            }

            navigate('/onboarding/corporate');
            return; // STOP HERE - don't call SUBUTAI
          }
        }
      } catch (corporateError) {
        // If corporate check fails, user is not CORPORATE, continue to SUBUTAI
        console.log('ℹ️ Not a corporate user, continuing to SUBUTAI...');
      }

      // Get referral code from URL or localStorage if present
      const pathParts = window.location.pathname.split('/');
      const pathReferralCode = pathParts.find(part => part.match(/^[A-Z0-9]{6,}$/));
      const storedReferralCode = localStorage.getItem('pendingReferralCode');
      const referralCode = pathReferralCode || storedReferralCode || null;

      // Check if this is an upgrade from EXPRESS to INDIVIDUAL
      const isUpgrade = localStorage.getItem('upgradeFromExpress') === 'true';
      console.log('🔄 ONBOARDING: isUpgrade flag:', isUpgrade);
      console.log('🔄 ONBOARDING: referralCode:', referralCode);

      // Clear the stored referral code after using it
      if (storedReferralCode) {
        localStorage.removeItem('pendingReferralCode');
      }

      // NO eliminar el flag de upgrade aquí - lo necesitamos durante todo el proceso
      // Solo se eliminará cuando el onboarding se complete con éxito

      console.log('📡 ONBOARDING: Llamando a onboardingAPI.startChat con isUpgrade:', isUpgrade);
      const response = await onboardingAPI.startChat(referralCode, isUpgrade);
      console.log('✅ ONBOARDING: Respuesta recibida:', response);

      // 🔍 DEBUG: Log detallado de la respuesta
      console.log('🔍 ONBOARDING DEBUG - Desglose de respuesta:');
      console.log('  - success:', response.success);
      console.log('  - data exists:', !!response.data);
      console.log('  - message exists:', !!response.data?.message);
      console.log('  - message length:', response.data?.message?.length || 0);
      console.log('  - message preview:', response.data?.message?.substring(0, 100));
      console.log('  - currentStage:', response.data?.currentStage);
      console.log('  - requiresDocument:', response.data?.requiresDocument);
      console.log('  - documentType:', response.data?.documentType);
      console.log('  - suggestedActions:', response.data?.suggestedActions);

      // 🏢 EXTRA SAFETY: Check if SUBUTAI says it's CORPORATE
      if (response.data?.currentStage === 'CORPORATE_WEB_FORM') {
        console.log('🏢 SUBUTAI returned CORPORATE_WEB_FORM - redirecting to web form');
        navigate('/onboarding/corporate');
        return;
      }

      if (response.success && response.data) {
        // 🚨 FIX: Verificar que el mensaje existe, si no, usar mensaje por defecto
        const messageContent = response.data.message || 'Bienvenido al proceso de onboarding. Por favor, selecciona tu idioma.';

        console.log('📝 ONBOARDING: Setting message:', messageContent);
        setState(prev => ({
          ...prev,
          sessionId: response.data.sessionId,
          currentStage: response.data.currentStage || 'LANGUAGE_SELECTION',
          preferredLanguage: response.data.preferredLanguage || prev.preferredLanguage
        }));

        const welcomeMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: messageContent,  // 🚨 FIX: Usar messageContent en lugar de response.data.message
          timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
        console.log('✅ ONBOARDING: Message set in state, messages array:', [welcomeMessage]);

        if (response.data.suggestedActions) {
          setSuggestedActions(response.data.suggestedActions);
        }

        // Check if terms button should be shown
        if (response.data.showTermsButton) {
          setShowTermsButton(true);
        } else {
          setShowTermsButton(false);
        }

        // ✅ IMPORTANTE: Check if document is required from startChat response
        if (response.data.requiresDocument && response.data.documentType) {
          console.log('🔍 ONBOARDING: Document required from startChat:', response.data.documentType);
          setRequiredDocumentType(response.data.documentType);
        } else {
          setRequiredDocumentType('');
        }

        if (response.data.isComplete || response.data.redirectToDashboard || response.data.redirectToLogin) {
          // Limpiar flag de upgrade si existe (onboarding completado exitosamente)
          console.log('🎉 ONBOARDING: Completado exitosamente - limpiando flag de upgrade');
          localStorage.removeItem('upgradeFromExpress');

          // Handle logout redirect for completed onboarding
          if (response.data.redirectToLogin) {
            const loginUrl = response.data.loginUrl || 'https://banking.belobaba.io';
            const redirectDelay = response.data.redirectDelay || 5000;  // 5 seconds

            // Clear ALL auth data and cache to ensure clean logout
            // User must login manually - NO auto-login
            localStorage.clear();
            sessionStorage.clear();

            // Force hard redirect to login page after delay (bypass any routing)
            setTimeout(() => {
              window.location.replace("/");  // Redirect to login WITHOUT token
            }, redirectDelay);
          } else if (response.data.redirectToDashboard) {
            // Legacy dashboard redirect (kept for compatibility)
            if (response.data.authToken) {
              localStorage.setItem('token', response.data.authToken);
              localStorage.setItem('isAuthenticated', 'true');
            }

            const dashboardUrl = response.data.dashboardUrl || '/dashboard';
            const redirectDelay = response.data.redirectDelay || 2000;

            if (dashboardUrl.startsWith('http')) {
              setTimeout(() => {
                window.location.href = dashboardUrl;
              }, redirectDelay);
            } else {
              setTimeout(() => navigate(dashboardUrl), redirectDelay);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to start chat:', error);
      setMessages([{
        id: Date.now().toString(),
        role: 'system',
        content: 'Failed to start onboarding. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input;
    if (!textToSend.trim() || !state.sessionId) return;

    // Add user message to chat
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: textToSend,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSuggestedActions([]);
    setLoading(true);

    try {
      const response = await onboardingAPI.sendMessage(state.sessionId, textToSend);
      console.log(response)
      if (response.success) {
        const data = response.data;
        
        // Add assistant response
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.message,
          timestamp: new Date(),
        }]);

        // Update state
        if (data.currentStage) {
          setState(prev => ({ ...prev, currentStage: data.currentStage }));
        }
        
        // Update language if provided
        if (data.preferredLanguage) {
          setState(prev => ({ ...prev, preferredLanguage: data.preferredLanguage }));
          // console.log('🌍 Language updated from backend:', data.preferredLanguage);
        }

        // Update suggested actions
        if (data.suggestedActions) {
          setSuggestedActions(data.suggestedActions);
        }

        // Check if terms button should be shown
        if (data.showTermsButton) {
          setShowTermsButton(true);
        } else {
          setShowTermsButton(false);
        }

        // Check if document is required
        if (data.requiresDocument && data.documentType) {
          setRequiredDocumentType(data.documentType);
          // Don't open modal automatically - user should click the + button
        } else {
          // ✅ FIX: Clear document requirement when response doesn't require a document
          setRequiredDocumentType('');
        }
        // 🏢 NEW: Check if user selected CORPORATE and needs to go to web form
        if (data.redirectToCorporateForm) {
          console.log('🏢 [CORPORATE] Redirecting to corporate web form');
          // Store language for the corporate form
          localStorage.setItem('corporateOnboardingLanguage', data.language || 'en');
          // Redirect after showing the message
          setTimeout(() => {
            navigate('/onboarding/corporate');
          }, 3000);
        }

        // Check if onboarding is complete or needs redirect
        if (data.isComplete || data.redirectToDashboard || data.redirectToLogin) {
          // Handle logout redirect for completed onboarding
          if (data.redirectToLogin) {
            const loginUrl = data.loginUrl || 'https://banking.belobaba.io';
            const redirectDelay = data.redirectDelay || 5000;  // 5 seconds

            // Clear ALL auth data and cache to ensure clean logout
            // User must login manually - NO auto-login
            localStorage.clear();
            sessionStorage.clear();

            // Force hard redirect to login page after delay (bypass any routing)
            setTimeout(() => {
              window.location.replace("/");  // Redirect to login WITHOUT token
            }, redirectDelay);
          } else if (data.redirectToDashboard) {
            // Legacy dashboard redirect (kept for compatibility)
            if (data.authToken) {
              localStorage.setItem('token', data.authToken);
              localStorage.setItem('isAuthenticated', 'true');
            }

            const dashboardUrl = data.dashboardUrl || '/dashboard';
            const redirectDelay = data.redirectDelay || 2000;

            if (dashboardUrl.startsWith('http')) {
              setTimeout(() => {
                window.location.href = dashboardUrl;
              }, redirectDelay);
            } else {
              setTimeout(() => navigate(dashboardUrl), redirectDelay);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'system',
        content: 'Failed to send message. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (file: File, documentType: string) => {
    // 🔒 PROTECTION: Prevent multiple simultaneous uploads
    if (loading) {
      console.log('⚠️ Upload already in progress, ignoring duplicate request');
      return;
    }

    setShowDocumentUpload(false);
    setLoading(true);

    // Add upload message
    const uploadMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: `Uploading ${documentType}: ${file.name}`,
      timestamp: new Date(),
      documentType,
    };
    setMessages(prev => [...prev, uploadMessage]);

    try {
      const response = await onboardingAPI.uploadDocument(state.sessionId, documentType, file);

      if (response.success) {
        // ✅ FIX: Track document progress ONLY after successful upload
        setState(prev => ({
          ...prev,
          documentProgress: [...prev.documentProgress, documentType]
        }));

        // Add success message
        const successMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: new Date(),
          documentInfo: response.extractedData,
        };
        setMessages(prev => [...prev, successMessage]);

        // Update suggested actions
        if (response.suggestedActions) {
          setSuggestedActions(response.suggestedActions);
        }

        // Check if more documents are needed
        if (response.requiresDocument && response.documentType) {
          setRequiredDocumentType(response.documentType);
          // Don't open modal automatically - user should click the + button
        } else {
          // ✅ FIX: Clear document requirement when response doesn't require more documents
          setRequiredDocumentType('');
        }
      } else {
        // ❌ VALIDATION FAILED: Show error message and keep upload button active
        console.log('⚠️ Document validation failed:', response);

        // Add error message to chat
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message || '❌ Documento inválido. Por favor, sube el documento correcto.',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);

        // Keep the upload button active by maintaining requiresDocument state
        if (response.requiresDocument && response.documentType) {
          setRequiredDocumentType(response.documentType);
          console.log('🔄 Keeping upload button active for document type:', response.documentType);
        }
      }
    } catch (error) {
      console.error('Document upload failed:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'system',
        content: 'Failed to upload document. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestedAction = (action: string) => {
    sendMessage(action);
  };

  const handleOpenTerms = async () => {
    try {
      setLoading(true);
      // Fetch signed URL from backend
      const response = await fetch(`${getCurrentApiUrl()}/ai/terms-pdf-url`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success && data.url) {
        // Open signed URL in new tab
        window.open(data.url, '_blank');
      } else {
        throw new Error('Failed to get PDF URL');
      }
    } catch (error) {
      console.error('Error opening terms PDF:', error);
      alert('Failed to load Terms and Conditions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDocumentTypeLabel = (type: string): string => {
    const lang = state.preferredLanguage || 'en';

    const labels: Record<string, Record<string, string>> = {
      // ========== DOCUMENTOS DE IDENTIDAD (INDIVIDUAL + CORPORATE REPS) ==========
      'passport': {
        'es': 'Pasaporte',
        'en': 'Passport',
        'pt': 'Passaporte',
        'fr': 'Passeport',
        'de': 'Reisepass',
        'it': 'Passaporto',
        'nl': 'Paspoort',
        'ru': 'Паспорт',
        'ar': 'جواز السفر'
      },
      'id_front': {
        'es': 'Frente del DNI/ID',
        'en': 'ID Card Front',
        'pt': 'Frente do Documento',
        'fr': 'Recto de la Carte d\'Identité',
        'de': 'Vorderseite des Ausweises',
        'it': 'Fronte della Carta d\'Identità',
        'nl': 'Voorkant ID-kaart',
        'ru': 'Лицевая сторона удостоверения',
        'ar': 'الجهة الأمامية من بطاقة الهوية'
      },
      'id_back': {
        'es': 'Reverso del DNI/ID',
        'en': 'ID Card Back',
        'pt': 'Verso do Documento',
        'fr': 'Verso de la Carte d\'Identité',
        'de': 'Rückseite des Ausweises',
        'it': 'Retro della Carta d\'Identità',
        'nl': 'Achterkant ID-kaart',
        'ru': 'Обратная сторона удостоверения',
        'ar': 'الجهة الخلفية من بطاقة الهوية'
      },
      'license_front': {
        'es': 'Frente de Licencia de Conducir',
        'en': 'Driver\'s License Front',
        'pt': 'Frente da Carteira de Motorista',
        'fr': 'Recto du Permis de Conduire',
        'de': 'Vorderseite des Führerscheins',
        'it': 'Fronte della Patente di Guida',
        'nl': 'Voorkant Rijbewijs',
        'ru': 'Лицевая сторона водительских прав',
        'ar': 'الجهة الأمامية من رخصة القيادة'
      },
      'license_back': {
        'es': 'Reverso de Licencia de Conducir',
        'en': 'Driver\'s License Back',
        'pt': 'Verso da Carteira de Motorista',
        'fr': 'Verso du Permis de Conduire',
        'de': 'Rückseite des Führerscheins',
        'it': 'Retro della Patente di Guida',
        'nl': 'Achterkant Rijbewijs',
        'ru': 'Обратная сторона водительских прав',
        'ar': 'الجهة الخلفية من رخصة القيادة'
      },
      'selfie': {
        'es': 'Selfie con Documento',
        'en': 'Selfie with Document',
        'pt': 'Selfie com Documento',
        'fr': 'Selfie avec Document',
        'de': 'Selfie mit Dokument',
        'it': 'Selfie con Documento',
        'nl': 'Selfie met Document',
        'ru': 'Селфи с документом',
        'ar': 'صورة سيلفي مع المستند'
      },
      'selfie_with_id': {
        'es': 'Selfie con Documento de Identidad',
        'en': 'Selfie with ID Document',
        'pt': 'Selfie com Documento de Identidade',
        'fr': 'Selfie avec Pièce d\'Identité',
        'de': 'Selfie mit Ausweis',
        'it': 'Selfie con Documento d\'Identità',
        'nl': 'Selfie met Identiteitsbewijs',
        'ru': 'Селфи с удостоверением личности',
        'ar': 'صورة سيلفي مع وثيقة الهوية'
      },

      // ========== COMPROBANTES DE DOMICILIO (INDIVIDUAL + CORPORATE REPS) ==========
      'utility_bill': {
        'es': 'Comprobante de Domicilio',
        'en': 'Proof of Address (Utility Bill)',
        'pt': 'Comprovante de Residência',
        'fr': 'Justificatif de Domicile',
        'de': 'Adressnachweis (Rechnung)',
        'it': 'Prova di Indirizzo',
        'nl': 'Adresbewijs',
        'ru': 'Подтверждение адреса',
        'ar': 'إثبات العنوان'
      },
      'proof_of_address': {
        'es': 'Comprobante de Domicilio',
        'en': 'Proof of Address',
        'pt': 'Comprovante de Endereço',
        'fr': 'Justificatif de Domicile',
        'de': 'Adressnachweis',
        'it': 'Prova di Indirizzo',
        'nl': 'Adresbewijs',
        'ru': 'Подтверждение адреса',
        'ar': 'إثبات العنوان'
      },

      // ========== ESTADOS DE CUENTA (INDIVIDUAL + CORPORATE REPS) ==========
      'bank_statement': {
        'es': 'Estado de Cuenta Bancario',
        'en': 'Bank Statement',
        'pt': 'Extrato Bancário',
        'fr': 'Relevé Bancaire',
        'de': 'Kontoauszug',
        'it': 'Estratto Conto Bancario',
        'nl': 'Bankafschrift',
        'ru': 'Выписка из банка',
        'ar': 'كشف حساب بنكي'
      },

      // ========== DOCUMENTOS CORPORATIVOS (EMPRESA) ==========
      'incorporation_certificate': {
        'es': 'Certificado de Incorporación',
        'en': 'Certificate of Incorporation',
        'pt': 'Certificado de Incorporação',
        'fr': 'Certificat de Constitution',
        'de': 'Gründungsurkunde',
        'it': 'Certificato di Costituzione',
        'nl': 'Oprichtingsakte',
        'ru': 'Свидетельство о регистрации',
        'ar': 'شهادة التأسيس'
      },
      'articles_incorporation': {
        'es': 'Estatutos Sociales',
        'en': 'Articles of Incorporation',
        'pt': 'Estatuto Social',
        'fr': 'Statuts de la Société',
        'de': 'Gesellschaftsvertrag',
        'it': 'Atto Costitutivo',
        'nl': 'Statuten',
        'ru': 'Устав компании',
        'ar': 'النظام الأساسي'
      },
      'articles_of_incorporation': {
        'es': 'Artículos de Incorporación',
        'en': 'Articles of Incorporation',
        'pt': 'Artigos de Incorporação',
        'fr': 'Actes de Constitution',
        'de': 'Gründungsurkunde',
        'it': 'Atto Costitutivo',
        'nl': 'Oprichtingsakten',
        'ru': 'Учредительные документы',
        'ar': 'مواد التأسيس'
      },
      'business_registration': {
        'es': 'Registro Mercantil',
        'en': 'Business Registration',
        'pt': 'Registro Comercial',
        'fr': 'Registre de Commerce',
        'de': 'Handelsregister',
        'it': 'Registro delle Imprese',
        'nl': 'Handelsregister',
        'ru': 'Регистрация бизнеса',
        'ar': 'السجل التجاري'
      },
      'business_utility_bill': {
        'es': 'Comprobante de Domicilio Fiscal',
        'en': 'Business Proof of Address',
        'pt': 'Comprovante de Endereço Comercial',
        'fr': 'Justificatif d\'Adresse Professionnelle',
        'de': 'Geschäftsadressnachweis',
        'it': 'Prova di Indirizzo Aziendale',
        'nl': 'Zakelijk Adresbewijs',
        'ru': 'Подтверждение адреса компании',
        'ar': 'إثبات عنوان الشركة'
      },
      'business_proof_address': {
        'es': 'Comprobante de Domicilio Comercial',
        'en': 'Business Proof of Address',
        'pt': 'Comprovante de Endereço Empresarial',
        'fr': 'Justificatif d\'Adresse d\'Entreprise',
        'de': 'Geschäftsadresse Nachweis',
        'it': 'Prova di Indirizzo Commerciale',
        'nl': 'Bedrijfsadresbewijs',
        'ru': 'Подтверждение бизнес-адреса',
        'ar': 'إثبات العنوان التجاري'
      },
      'company_registration': {
        'es': 'Registro de la Empresa',
        'en': 'Company Registration',
        'pt': 'Registro da Empresa',
        'fr': 'Enregistrement de la Société',
        'de': 'Unternehmensregistrierung',
        'it': 'Registrazione dell\'Azienda',
        'nl': 'Bedrijfsregistratie',
        'ru': 'Регистрация компании',
        'ar': 'تسجيل الشركة'
      },
      'certificate_of_incorporation': {
        'es': 'Certificado de Constitución',
        'en': 'Certificate of Incorporation',
        'pt': 'Certificado de Constituição',
        'fr': 'Certificat de Création',
        'de': 'Gründungszertifikat',
        'it': 'Certificato di Incorporazione',
        'nl': 'Incorporatiecertificaat',
        'ru': 'Свидетельство об учреждении',
        'ar': 'شهادة التأسيس'
      }
    };

    const defaultLabel: Record<string, string> = {
      'es': 'Documento',
      'en': 'Document',
      'pt': 'Documento',
      'fr': 'Document',
      'de': 'Dokument',
      'it': 'Documento',
      'nl': 'Document',
      'ru': 'Документ',
      'ar': 'مستند'
    };

    return labels[type]?.[lang] || labels[type]?.['en'] || defaultLabel[lang] || defaultLabel['en'];
  };

   const handleLogout = async() => {
    console.log("test")
    logout();

  };

  const handleRestartOnboarding = async () => {
    // Confirm with user before restarting
    const restartMessages: Record<string, string> = {
      'es': '¿Estás seguro de que deseas reiniciar el onboarding desde el principio? Perderás todo el progreso actual.',
      'en': 'Are you sure you want to restart the onboarding from the beginning? You will lose all current progress.',
      'pt': 'Tem certeza de que deseja reiniciar o onboarding desde o início? Você perderá todo o progresso atual.',
      'fr': 'Êtes-vous sûr de vouloir redémarrer l\'intégration depuis le début? Vous perdrez toute progression actuelle.',
      'de': 'Sind Sie sicher, dass Sie das Onboarding von Anfang an neu starten möchten? Sie verlieren den gesamten aktuellen Fortschritt.',
      'it': 'Sei sicuro di voler riavviare l\'onboarding dall\'inizio? Perderai tutti i progressi attuali.',
      'nl': 'Weet je zeker dat je het onboarding opnieuw wilt starten vanaf het begin? Je verliest alle huidige voortgang.',
      'ru': 'Вы уверены, что хотите перезапустить процесс онбординга с самого начала? Вы потеряете весь текущий прогресс.',
      'ar': 'هل أنت متأكد من أنك تريد إعادة بدء عملية الانضمام من البداية؟ سوف تفقد كل التقدم الحالي.'
    };

    const confirmMessage = restartMessages[state.preferredLanguage] || restartMessages['en'];

    if (window.confirm(confirmMessage)) {
      console.log('🔄 ONBOARDING: User requested restart - deleting all conversations');

      setLoading(true);

      try {
        // 1. Llamar al endpoint para borrar todas las conversaciones del backend (DynamoDB + Redis)
        const token = localStorage.getItem('token');
        const response = await fetch(`${getCurrentApiUrl()}/ai/subutai/conversations`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        console.log(`✅ ONBOARDING: Backend deleted ${data.deleted || 0} conversations`);

      } catch (error) {
        console.error('❌ ONBOARDING: Error deleting conversations from backend:', error);
        // Continuar de todas formas con la limpieza local
      }

      // 2. Clear all state
      setState({
        sessionId: '',
        currentStage: 'INITIAL',
        preferredLanguage: 'en',
        documentProgress: []
      });

      // 3. Clear messages and UI
      setMessages([]);
      setSuggestedActions([]);
      setInput('');
      setShowDocumentUpload(false);
      setRequiredDocumentType('');
      setShowTermsButton(false);

      // 4. Clear any upgrade flags
      localStorage.removeItem('upgradeFromExpress');
      localStorage.removeItem('pendingReferralCode');

      // 5. Reset the startChat flag and restart
      startChatCalled.current = false;

      console.log('✅ ONBOARDING: State cleared - restarting...');

      setLoading(false);

      // 6. Restart the onboarding
      startChat();
    }
  };

  // Function to clean markdown formatting and convert URLs to links
  const renderMessageContent = (content: string) => {
    // Handle undefined or null content
    if (!content) return '';

    // Clean markdown bold markers
    const cleanedContent = content.replace(/\*\*/g, '');

    // Convert URLs to clickable links that open in new tab
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = cleanedContent.split(urlRegex);

    return (
      <>
        {parts.map((part, index) => {
          if (part.match(urlRegex)) {
            return (
              <a
                key={index}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#2196F3',
                  textDecoration: 'underline',
                  wordBreak: 'break-all'
                }}
              >
                {part}
              </a>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };
  // useEffect(()=>{
  //   if(state.currentStage==="COMPLETED"){
  //     navigate("/dashboard")
  //   }
  // },[state])
  return (
    <div style={{ 
      height: '100vh',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '90vh',
        maxWidth: '900px',
        width: '100%',
        margin: '20px',
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ 
          padding: '20px', 
          borderBottom: '1px solid #ddd',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <img src={Logo} alt="Belobaba" className="ml-0 ms:ml-11 logo" />
            <h3 style={{ margin: 0 }}>Chat with SUBUTAI</h3>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={handleRestartOnboarding}
              style={{
                padding: '5px 10px',
                fontSize: '12px',
                backgroundColor: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
              title={state.preferredLanguage === 'es' ? 'Reiniciar Onboarding desde el principio' :
                     state.preferredLanguage === 'pt' ? 'Reiniciar Onboarding do início' :
                     state.preferredLanguage === 'fr' ? 'Redémarrer l\'Onboarding depuis le début' :
                     state.preferredLanguage === 'de' ? 'Onboarding von vorne starten' :
                     state.preferredLanguage === 'it' ? 'Riavvia Onboarding dall\'inizio' :
                     state.preferredLanguage === 'nl' ? 'Onboarding opnieuw starten' :
                     state.preferredLanguage === 'ru' ? 'Перезапустить Онбординг сначала' :
                     state.preferredLanguage === 'ar' ? 'إعادة بدء الانضمام من البداية' :
                     'Restart Onboarding from beginning'}
            >
              🔄 Reset
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: '5px 10px',
                fontSize: '12px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer'
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Progress Indicator */}
        {state.accountType === 'INDIVIDUAL' && state.documentProgress.length > 0 && (
          <div style={{
            padding: '10px 20px',
            backgroundColor: '#f0f7ff',
            borderBottom: '1px solid #ddd'
          }}>
            <div style={{ fontSize: '12px', marginBottom: '5px', fontWeight: 'bold' }}>
              Progress - INDIVIDUAL Account
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {['id_front', 'id_back', 'passport', 'license_front', 'license_back', 'selfie', 'utility_bill', 'bank_statement'].map(doc => {
                const isCompleted = state.documentProgress.includes(doc);
                return (
                  <div
                    key={doc}
                    style={{
                      padding: '3px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      backgroundColor: isCompleted ? '#4CAF50' : '#e0e0e0',
                      color: isCompleted ? 'white' : '#666'
                    }}
                  >
                    {isCompleted ? '✓' : '○'} {getDocumentTypeLabel(doc)}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Messages */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '20px',
          backgroundColor: '#fafafa'
        }}>
          {messages.map((message) => (
            <div
              key={message.id}
              style={{
                marginBottom: '15px',
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
              }}
            >
              <div
                style={{
                  maxWidth: '70%',
                  padding: '10px 15px',
                  borderRadius: '10px',
                  backgroundColor: 
                    message.role === 'user' ? '#4CAF50' : 
                    message.role === 'system' ? '#ff9800' : '#e0e0e0',
                  color: message.role === 'user' ? 'white' : 'black',
                }}
              >
                <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                  {renderMessageContent(message.content)}
                </div>
                {message.documentInfo && (
                  <div style={{
                    marginTop: '10px',
                    padding: '5px',
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    borderRadius: '5px',
                    fontSize: '12px'
                  }}>
                    <strong>Extracted Data:</strong>
                    <pre style={{ margin: 0, fontSize: '11px' }}>
                      {JSON.stringify(message.documentInfo, null, 2)}
                    </pre>
                  </div>
                )}
                <div style={{ 
                  fontSize: '11px', 
                  marginTop: '5px', 
                  opacity: 0.7 
                }}>
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Actions */}
        {suggestedActions.length > 0 && (
          <div style={{
            padding: '10px 20px',
            borderTop: '1px solid #ddd',
            backgroundColor: '#f5f5f5'
          }}>
            <div style={{ fontSize: '12px', marginBottom: '5px', color: '#666' }}>
              Suggested actions:
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {suggestedActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedAction(action)}
                  disabled={loading}
                  style={{
                    padding: '5px 15px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    border: '1px solid #4CAF50',
                    borderRadius: '15px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    color: '#4CAF50'
                  }}
                >
                  {action}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Terms and Conditions Button */}
        {showTermsButton && (
          <div style={{
            padding: '10px 20px',
            borderTop: '1px solid #ddd',
            backgroundColor: '#e3f2fd',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <button
              onClick={handleOpenTerms}
              disabled={loading}
              style={{
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: 'bold',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = '#1976D2';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#2196F3';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              📄 {state.preferredLanguage === 'es' ? 'Abrir Términos y Condiciones' :
                  state.preferredLanguage === 'pt' ? 'Abrir Termos e Condições' :
                  state.preferredLanguage === 'fr' ? 'Ouvrir les Conditions Générales' :
                  state.preferredLanguage === 'de' ? 'Geschäftsbedingungen öffnen' :
                  state.preferredLanguage === 'it' ? 'Apri Termini e Condizioni' :
                  state.preferredLanguage === 'nl' ? 'Algemene Voorwaarden openen' :
                  state.preferredLanguage === 'ru' ? 'Открыть Условия' :
                  state.preferredLanguage === 'ar' ? 'فتح الشروط والأحكام' :
                  'Open Terms and Conditions'}
            </button>
          </div>
        )}

        {/* Input Area */}
        <div style={{ 
          padding: '20px', 
          borderTop: '1px solid #ddd',
          backgroundColor: 'white'
        }}>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
          >
            {/* Document Upload Button - Show when document is required */}
            {requiredDocumentType && (
              <button
                type="button"
                onClick={() => setShowDocumentUpload(true)}
                style={{
                  width: '45px',
                  height: '45px',
                  fontSize: '24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
                title="Upload Document"
              >
                +
              </button>
            )}
            
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                boxSizing: 'border-box'
              }}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !input.trim() ? 0.7 : 1
              }}
            >
              {loading ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>

        {/* Document Upload Modal */}
        {showDocumentUpload && (
          <DocumentUpload
            documentType={requiredDocumentType}
            onUpload={handleDocumentUpload}
            onCancel={() => setShowDocumentUpload(false)}
            language={state.preferredLanguage}
          />
        )}

        {/* PDF Modal for Terms and Conditions */}
        {showPdfModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}>
            <div style={{
              width: '90%',
              height: '90%',
              backgroundColor: 'white',
              borderRadius: '10px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '15px 20px',
                borderBottom: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f5f5f5'
              }}>
                <h3 style={{ margin: 0 }}>Terms and Conditions</h3>
                <button
                  onClick={() => setShowPdfModal(false)}
                  style={{
                    padding: '8px 15px',
                    fontSize: '14px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </div>
              
              {/* PDF Viewer */}
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <iframe
                  src={pdfUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title="Terms and Conditions"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default OnboardingForm;